/**
 * Live breaks' default copy (the rule is in `shared.ts`): first the words every live-break surface
 * says (a live break's status, its directory card, the queue, the priority note and the queue
 * dialogs), then each page's own, by section: the directory's here, the live break page's after it.
 *
 * Boxed calls a purchase a "break" ("Confirm Break", "Your Breaks"); a purchase here is a queue
 * entry, its turn on stage an Opening, and "break" is never said alone (CONTEXT.md). What a
 * purchase holds and that an Opening may not reach the stream are the platform's
 * (`liveBreakDisclosure.ts`), and refusals, load failures and toasts stay with the storefront.
 */
import {
  LIVE_BREAK_MAX_BOXES,
  LIVE_BREAK_MAX_OPENER_DELAY_MS,
  LIVE_BREAK_TITLE_MAX,
  type LiveBreakNotAcceptingReason,
  type LiveBreakRevealPhase,
} from "../liveBreaks.js";
import type { LiveBreakViewerPriorityStatus } from "../liveBreakViews.js";
import { capitalizeTerm, type StorefrontTerminology } from "../storefrontTerminology.js";
import { STOREFRONT_PAGING_COPY } from "./shared.js";

/** A count as the card shows it: "1,204". */
function countLabel(count: number): string {
  return count.toLocaleString("en-US");
}

/** A queue position as the queue shows it: "#3". */
function placeLabel(position: number): string {
  return `#${position}`;
}

/** A count of places in the queue: "1 place", "4 places". */
function placesLabel(places: number): string {
  return `${places} ${places === 1 ? "place" : "places"}`;
}

/** How one entry reads inside a sentence (chat's rows): "2× Tide Pool". A list's is the queue's `entry`. */
function entryLabel(amount: number, boxName: string): string {
  return `${amount}× ${boxName}`;
}

/** The note's one action, which the storefront wires: retry the check, link Twitch, subscribe. */
export type LiveBreakPriorityActionKind = "retry" | "connect" | "reconnect" | "subscribe";

/** The join dialog's note about Twitch subscriber Priority, for one priority status. */
export interface LiveBreakPriorityNoteCopy {
  title: string;
  body: string;
  /** Its one action, when it has one. */
  action: {
    kind: LiveBreakPriorityActionKind;
    label: string;
    /** Read after the label by a screen reader: the action leaves the store. */
    hint?: string;
  } | null;
}

/** The words every live-break surface says of a live break, its queue and the shopper's entries. */
export interface LiveBreakCopy {
  /** A live break's badge (live, Draining, closed), and a queue row's for the entry Up Next. */
  status: { live: string; next: string; ending: string; closed: string };
  /** A live break as a card: the directory's, and the widget's rows. */
  card: {
    /** On a live break kept off the directory. */
    private: string;
    /** The viewer count, read by a screen reader. */
    viewers: (count: number) => string;
    /** The queue while it takes purchases, with the count queued. */
    queue: (count: number) => string;
    /**
     * While it takes no purchases, by why (the host stopped them, the host is away, or it drains
     * as it ends), with the count still waiting.
     */
    queueStopped: (reason: LiveBreakNotAcceptingReason, count: number) => string;
    /** Once it has closed. */
    queueClosed: string;
    /** Before its lowest price. */
    from: string;
    /** The host's stream, by the channel's name: on Twitch, and in picture-in-picture. */
    watchOnTwitch: (channel: string) => string;
    pictureInPicture: (channel: string) => string;
  };
  /** The queue's rows and the shopper's place in it. */
  queue: {
    /** The featured row's line: the entry on stage, and the one Up Next. */
    openingNow: string;
    upNext: string;
    /** The chip on an entry in the Priority tier. */
    priority: string;
    /** A waiting row's place: "#3". */
    place: (position: number) => string;
    /** Each row's accessible name, by the buyer's name: on stage, then by its place. */
    openingLabel: (name: string) => string;
    placeLabel: (position: number, name: string) => string;
    /** The shopper's own row. */
    you: string;
    /** A buyer with no name to read. */
    unknownBuyer: string;
    /** The host's row menu, by the buyer's name. */
    actions: (name: string) => string;
    /** One entry, its box and then how many, as the queue dialogs' chip reads: "Tide Pool ×2". */
    entry: (amount: number, boxName: string) => string;
    /** Said in place of the rows while nothing is queued. */
    empty: string;
  };
  /** The shopper's place strip: their soonest entry, and the way to all of them. */
  place: {
    /** "You're #3 in the queue"; null while the place is unknown. */
    inQueue: (position: number | null) => string;
    /**
     * The same place as shown, its number apart so it can stand out: "#", "3", "in the queue".
     * `inQueue` is still what is read.
     */
    inQueueParts?: (position: number) => { before: string; place: string; after: string };
    /** Opens Your entries. */
    manage: string;
    /** Its accessible name. */
    manageLabel: string;
    /** After the soonest entry, while the shopper holds more: "+2 more". */
    more: (count: number) => string;
  };
  /** The figures the queue dialogs show. */
  metrics: {
    estimatedPosition: string;
    currentPosition: string;
    estimatedWait: string;
    newEstimatedWait: string;
    /** A wait in minutes: "12 min". */
    minutes: (minutes: number) => string;
    /** How a new estimate moved: "(~10 min less)"; null when it didn't. */
    waitChange: (previousMinutes: number, minutes: number) => string | null;
    /** Read in place of a figure not known yet. */
    unavailable: string;
  };
  /** The join dialog's note on Priority; null where it says nothing (no priority here). */
  priorityNote: (
    status: LiveBreakViewerPriorityStatus,
    /** The channel's name, when known. */
    channel: string | null,
  ) => LiveBreakPriorityNoteCopy | null;
  /** Confirming a purchase: the shopper's, and the host's own. */
  join: {
    title: string;
    /** Where the purchase goes: the host's live break by name, or this one. */
    joins: (hostName: string | null) => string;
    /** The host's own purchase. */
    joinsYours: string;
    whatsInside: string;
    /** After the box's own price, which the total multiplies. */
    each: string;
    longWait: string;
    dontShowAgain: string;
    /** The shopper's confirm, beside the total. */
    confirm: string;
    /** The host's two confirms: the back of the queue, and the front lane. */
    hostConfirm: string;
    openNext: string;
    /** Under the host's two: how Open next differs from joining the queue. */
    openNextHint: string;
    cancel: string;
  };
  /** The shopper's queued entries in this live break. */
  entries: {
    title: string;
    /** Said in place of the list while nothing is queued. */
    empty: string;
    /** Names what an entry holds, beside its amount. */
    held: string;
    /** Each entry's way out: it opens the leave dialog for that entry. */
    manage: string;
    /** Its accessible name, by the entry's box. */
    manageLabel: (boxName: string) => string;
    /** Why an entry Up Next can't be cancelled. */
    upNext: string;
    close: string;
  };
  /** Leaving the queue: what is released is the platform's (`liveBreakReleaseLine`). */
  leave: {
    title: string;
    confirm: string;
    /** Opens an entry's box as it was bought; its accessible name, by the box. */
    viewBox: string;
    viewBoxLabel: (boxName: string) => string;
    /** The shopper's entry on stage is left out. */
    openingExcluded: string;
    /** Said while nothing can be cancelled, with Close the one way out. */
    nothingToCancel: string;
    close: string;
    cancel: string;
  };
  /** A notice that the shopper's entry is near the front. */
  almostYourTurn: {
    title: string;
    heading: string;
    body: (position: number) => string;
    /** How far it moved since the last notice. */
    moved: (from: number, to: number) => string;
    /** Out of the store's other pages, to the live break. */
    goToLiveBreak: string;
  };
  /** A notice that the shopper's place moved. */
  positionChanged: {
    title: string;
    /** The headline, by which way it moved and how far. */
    movedUp: (places: number) => string;
    movedBack: (places: number) => string;
    moved: string;
    body: (from: number, to: number) => string;
    gotIt: string;
    goToLiveBreak: string;
  };
  /**
   * A live break's rows in chat, which the platform posts and the store words: a purchase and a
   * cancel, and a big hit, in its room; the host's announcement in the site's chat.
   */
  chat: {
    /** Before the purchase's price: "Bought 2× Tide Pool for". */
    purchase: (amount: number, boxName: string) => string;
    /** A purchase that left the queue: "Left the queue with 2× Tide Pool". */
    cancel: (amount: number, boxName: string) => string;
    /** A big hit's headline, by its tier. */
    bigHit: { epic: string; legendary: string };
    /** Opens the box a big hit came from, to buy one. */
    viewBox: string;
    /** The announcement's headline, by the host's name. */
    announce: (hostName: string) => string;
    /** The announcement's way into the live break, and its accessible name by the title. */
    join: string;
    joinLabel: (title: string) => string;
  };
}

export function defaultLiveBreakCopy(terms: StorefrontTerminology): LiveBreakCopy {
  const { box } = terms;
  return {
    status: { live: "Live", next: "Next", ending: "Ending", closed: "Closed" },
    card: {
      private: "Private",
      viewers: (count) => `${count} signed-in ${count === 1 ? "viewer" : "viewers"}`,
      queue: (count) => `Queue • ${countLabel(count)}`,
      queueStopped: (reason, count) => {
        const waiting = count > 0 ? ` · ${countLabel(count)} waiting` : "";
        switch (reason) {
          case "host_away":
            return `Host away${waiting}`;
          case "draining":
            if (count === 0) return "No entries left";
            return count === 1 ? "Last entry" : `Last ${countLabel(count)} entries`;
          default:
            return `Not taking entries${waiting}`;
        }
      },
      queueClosed: "Queue closed",
      from: "From",
      watchOnTwitch: (channel) => `Watch ${channel} on Twitch`,
      pictureInPicture: (channel) => `Watch ${channel} in picture-in-picture`,
    },
    queue: {
      openingNow: "Opening now",
      upNext: "Up next",
      priority: "Priority",
      place: placeLabel,
      openingLabel: (name) => `Opening now: ${name}`,
      placeLabel: (position, name) => `Queue position ${position}: ${name}`,
      you: "You",
      unknownBuyer: "Player",
      actions: (name) => `Actions for ${name}`,
      entry: (amount, boxName) => `${boxName} ×${amount}`,
      empty: "Nobody is queued yet.",
    },
    place: {
      inQueue: (position) =>
        position === null ? "In the queue" : `You're ${placeLabel(position)} in the queue`,
      inQueueParts: (position) => ({ before: "#", place: String(position), after: "in the queue" }),
      manage: "Your entries",
      manageLabel: "Your entries",
      more: (count) => `+${countLabel(count)} more`,
    },
    metrics: {
      estimatedPosition: "Est. position",
      currentPosition: "Current position",
      estimatedWait: "Est. wait",
      newEstimatedWait: "New est. wait",
      minutes: (minutes) => `${minutes} min`,
      waitChange: (previousMinutes, minutes) => {
        const difference = minutes - previousMinutes;
        if (difference === 0) return null;
        return `(~${Math.abs(difference)} min ${difference < 0 ? "less" : "more"})`;
      },
      unavailable: "Not available",
    },
    priorityNote: (status, channel) => {
      const name = channel ?? "this live break's channel";
      const offer = `Subscribers to ${name} queue with Priority.`;
      const title = "Twitch subscribers get priority";
      switch (status) {
        case "priority":
          return {
            title: "Twitch subscriber priority",
            body: `You subscribe to ${name}, so this entry queues with Priority.`,
            action: null,
          };
        case "twitch_unavailable":
          return {
            title,
            body: `Couldn't check your Twitch subscription to ${name}, so this entry queues as standard.`,
            action: { kind: "retry", label: "Retry" },
          };
        case "twitch_not_linked":
          return { title, body: offer, action: { kind: "connect", label: "Connect Twitch" } };
        case "twitch_reconnect":
          return {
            title,
            body: `Reconnect your Twitch account to check your subscription to ${name}.`,
            action: { kind: "reconnect", label: "Reconnect Twitch" },
          };
        case "not_subscribed":
          return {
            title,
            body: offer,
            action: {
              kind: "subscribe",
              label: "Subscribe on Twitch",
              hint: "(opens in a new tab)",
            },
          };
        default:
          return null;
      }
    },
    join: {
      title: "Join the queue",
      joins: (hostName) =>
        hostName === null
          ? "This purchase joins this live break's queue."
          : `This purchase joins ${hostName}'s live break.`,
      joinsYours: "This purchase joins your live break.",
      whatsInside: "What's inside",
      each: "each",
      longWait: "Expect a long wait",
      dontShowAgain: "Don't show this again",
      confirm: "Buy & join queue",
      hostConfirm: "Buy & join queue",
      openNext: "Buy & open next",
      openNextHint: "Open next skips the queue: it opens right after the one on stage.",
      cancel: "Cancel",
    },
    entries: {
      title: "Your entries",
      empty: "You have nothing in the queue.",
      held: "Held",
      manage: "Leave queue…",
      manageLabel: (boxName) => `Leave queue with ${boxName}`,
      upNext: "You're up next, so this entry can't be cancelled.",
      close: "Close",
    },
    leave: {
      title: "Leave the queue",
      confirm: "Leave queue",
      viewBox: `View ${box.singular}`,
      viewBoxLabel: (boxName) => `View ${box.singular}: ${boxName}`,
      openingExcluded: "Your entry on stage can't be cancelled, so it isn't included.",
      nothingToCancel: "You have nothing in the queue to cancel.",
      close: "Close",
      cancel: "Cancel",
    },
    almostYourTurn: {
      title: "Almost your turn",
      heading: "Your opening is coming up!",
      body: (position) =>
        `You're now ${placeLabel(position)} in the queue. Stay nearby for your opening.`,
      moved: (from, to) =>
        `Your place in the queue changed from ${placeLabel(from)} to ${placeLabel(to)}.`,
      goToLiveBreak: "Go to the live break",
    },
    positionChanged: {
      title: "Queue position changed",
      movedUp: (places) => `You moved up ${placesLabel(places)}`,
      movedBack: (places) => `You moved back ${placesLabel(places)}`,
      moved: "Your place in the queue changed",
      body: (from, to) =>
        `Your place in the queue changed from ${placeLabel(from)} to ${placeLabel(to)}.`,
      gotIt: "Got it",
      goToLiveBreak: "Go to the live break",
    },
    chat: {
      purchase: (amount, boxName) => `Bought ${entryLabel(amount, boxName)} for`,
      cancel: (amount, boxName) => `Left the queue with ${entryLabel(amount, boxName)}`,
      bigHit: { epic: "Epic pull!", legendary: "Legendary pull!" },
      viewBox: `View ${box.singular}`,
      announce: (hostName) => `${hostName}'s live break is starting`,
      join: "Join",
      joinLabel: (title) => `Join ${title}`,
    },
  };
}

// The directory -------------------------------------------------------------------------------

/** The live breaks directory's words, by section, in the store's terms. */
export interface LiveBreaksDirectoryCopy {
  heading: {
    title: string;
    description: string;
    /** How many are open: "3 live now", with a + while more pages remain. */
    active: (count: number, more: boolean) => string;
    /** For a shopper who can host. */
    create: string;
    /** For a host, or anyone who has hosted. */
    hostDashboard: string;
    /** For anyone who has hosted. */
    history: string;
    /**
     * The help button's name, and the dialog it opens: what a live break is, then taking part in
     * three steps (the hold is the platform's, said after them), and for a shopper who can host,
     * on a phone, that hosting starts on a bigger screen.
     */
    howItWorks: {
      label: string;
      title: string;
      body: string;
      steps: readonly string[];
      hostOnLargerScreen: string;
      close: string;
    };
  };
  filter: {
    /** The chips' accessible name, and the chip for every category. */
    categories: string;
    all: string;
    search: { label: string; placeholder: string };
  };
  list: {
    /** Names the list's region. */
    label: string;
    /** Said while none is open at all: the chips and search step aside. */
    empty: { title: string; body: string };
    /** Said while the loaded ones match nothing: no more to load, or more to try. */
    noMatches: string;
    noMatchesLoaded: string;
    /** Under either: clears the search and the chips. */
    clearSearch: string;
    /** Load more's words while nothing loaded matches: the next page is searched too. */
    searchMore: string;
    /** Read while the first page loads, and while the next one does. */
    loading: string;
    loadingMore: string;
    loadMore: string;
    /** The count line announced after each page. */
    showing: (shown: number) => string;
    /** Beside a failed refresh, which keeps the cards it has. */
    retry: string;
  };
  /**
   * The page of a store whose own hosts go live, one live break at a time (`liveBreaks.hosting`
   * `store`): while the store's live break is open the page is that live break, so these are its
   * words while none is, in the store's name.
   */
  store: {
    /** The help dialog's steps, said of the store's one live break. */
    steps: (storeName: string) => readonly string[];
    offline: {
      title: (storeName: string) => string;
      body: (storeName: string) => string;
      /** For one of the store's hosts: opens Create's wizard. */
      goLive: string;
    };
  };
}

export function defaultLiveBreaksDirectoryCopy(
  terms: StorefrontTerminology,
): LiveBreaksDirectoryCopy {
  const { box } = terms;
  const description = `Open ${box.plural} together, live.`;
  return {
    heading: {
      title: "Live breaks",
      description,
      active: (count, more) => `${count}${more ? "+" : ""} live now`,
      create: "Create live break",
      hostDashboard: "Host dashboard",
      history: "Live break history",
      howItWorks: {
        label: "How live breaks work",
        title: "How live breaks work",
        body: description,
        steps: [
          "Pick a live break.",
          `Choose one of its ${box.plural} and join the queue.`,
          "Watch it opened live, in order, with everyone in the room.",
        ],
        hostOnLargerScreen: "Hosting a live break starts on a tablet or computer.",
        close: "Close",
      },
    },
    filter: {
      categories: "Live-break category",
      all: "All",
      search: { label: "Search live breaks", placeholder: "Search live breaks" },
    },
    list: {
      label: "Live breaks",
      empty: {
        title: "No live breaks right now",
        body: "Check back soon for the next live break.",
      },
      noMatches: "No live breaks match your search.",
      noMatchesLoaded: "No matches yet. Search more live breaks or change your filters.",
      clearSearch: "Clear search",
      searchMore: "Search more live breaks",
      loading: "Loading live breaks…",
      loadingMore: "Loading more live breaks…",
      loadMore: STOREFRONT_PAGING_COPY.loadMore,
      showing: STOREFRONT_PAGING_COPY.showing,
      retry: "Retry",
    },
    store: {
      steps: (storeName) => [
        `When ${storeName} goes live, its live break opens here.`,
        `Choose one of its ${box.plural} and join the queue.`,
        "Watch it opened live, in order, with everyone in the room.",
      ],
      offline: {
        title: (storeName) => `${storeName} isn't live right now`,
        body: (storeName) =>
          `Check back soon. When ${storeName} goes live, you can join the queue and watch ${box.plural} opened live.`,
        goLive: "Go live",
      },
    },
  };
}

// The live break page -------------------------------------------------------------------------

/**
 * The live break page's words, by section, in the store's terms. The shared words (the badge,
 * the queue's rows and the shopper's place, the queue dialogs) are `defaultLiveBreakCopy`'s. What
 * a purchase refuses and why (closed, Draining, the host away, Purchases stopped, a full queue),
 * the host commission in the prices, the closed page's countdown and a removed shopper's screen
 * are the platform's, and stay with the storefront's page.
 */
export interface LiveBreakRoomCopy {
  identity: {
    /** The way back to the directory, above the page on a wide screen. */
    back: string;
    /**
     * Its words on a store whose own live break the directory goes straight to, where every way
     * out of the page (this, the toolbar's close, the removed screen's) leads home instead.
     */
    home: { back: string; close: string };
    /** The viewer count's words, which open the roster. */
    viewers: (count: number) => string;
    /** Who is watching. */
    roster: { title: string; empty: string };
    /** Read after a person's name, which opens their public profile. */
    profile: string;
  };
  toolbar: {
    /** The tools' group name. */
    label: string;
    fairness: string;
    share: string;
    /** The sound toggle's one name, whichever way it is set. */
    sound: string;
    fullscreen: string;
    /** A phone's menu of the tools that don't fit. */
    more: string;
    /** A phone's way back to the directory, at the bar's end. */
    close: string;
    /** The host's way to show their stream, while none is set. */
    connect: string;
  };
  stage: {
    label: string;
    /** The Presentation's two lines; the host's second says it is theirs to start. */
    announce: readonly [string, string];
    hostAnnounce: readonly [string, string];
    /** How many of the box: "×2". */
    amount: (amount: number) => string;
    /** The Reveal's progress, from 1: "Opening 1 of 2". */
    opening: (current: number, total: number) => string;
    results: string;
    total: string;
    /** Over what the entry on stage cost (its price × how many), in the Presentation and Reveal. */
    spent: string;
    /** After one box's price beside how many: "×2 · $25.39 each". */
    each: string;
    /** The strip of pulls landed so far. */
    pulls: string;
    /** A phone's carousel of the results. */
    carousel: {
      label: string;
      previous: string;
      next: string;
      slide: (position: number, count: number) => string;
    };
    /** Auto-play's countdown to the next Presentation. */
    countdown: (seconds: number) => string;
    /** In the countdown's place once it reaches 0 and the next Presentation is due. */
    countdownNow: string;
  };
  buy: {
    label: string;
    carousel: { label: string; previous: string; next: string; show: (name: string) => string };
    quantity: string;
    whatsInside: string;
    /** The purchase beside its total, wherever it sits (the stage, the side panel, a sheet). */
    join: string;
    signIn: string;
    /** Any eligible box, where the live break is not restricted to its own. */
    elsewhere: string;
    empty: string;
  };
  breaks: {
    /** The panel's name, the store's word for its boxes. */
    title: string;
    list: { label: string; heading: string; empty: string };
    /** A phone's browser: its name, and its two tabs. */
    tabs: { label: string; boxes: string; queue: (count: number) => string };
  };
  queue: {
    title: string;
    /** Beside the title: "7 queued". */
    summary: (count: number) => string;
    /** The list's name. */
    label: string;
  };
  chat: {
    /** The handle that brings the chat up over a phone's stage. */
    sheet: string;
    /** Under a phone's chat: the buttons that open the browser, and its name and close. */
    browse: { boxes: string; queue: (count: number) => string; title: string; close: string };
    /** The room's chat: its name, its messages' name, and the way down to the newest. */
    title: string;
    transcript: string;
    jump: string;
    /** Who's watching, beside the title: "64 watching". */
    watching: (count: number) => string;
    /** Said in place of the messages before anyone has written, and while they load. */
    empty: string;
    loading: string;
    /** The host's tag beside their name. */
    host: string;
    /** A message's tools: react with an emote (and the tray's name), and reply, by the sender. */
    react: string;
    reactWith: string;
    reply: (name: string) => string;
    /** Above the message box while answering one, and its way out. */
    replyingTo: string;
    cancelReply: string;
    /** A reply's quote, which jumps to the message it answers. */
    showReplied: string;
    deleted: string;
    /** The message box's emote button, and the tray it opens. */
    emote: string;
  };
  results: {
    title: string;
    /** The figures beside the title, read in full. */
    summary: (openings: number, items: number, total: string) => string;
    /** Names the pulls' grid, and is read while it loads. */
    items: string;
    loading: string;
    retry: string;
    claim: string;
    claimAll: string;
    sellAll: string;
    sellSelected: string;
    allSold: string;
    sell: string;
    sold: string;
    /** A claimed pull that is in the shopper's inventory, and the link there. */
    inInventory: string;
    /** A phone's tab on the stage's edge: its count of what is new, read with it. */
    unseen: (count: number) => string;
    close: string;
  };
  /** The host's panel and sheet: its name, the phone's labelled button that opens it, and close. */
  hostControls: { title: string; trigger: string; close: string };
  /** "Want to open something else?": any eligible box, searched, at the live break's prices. */
  elsewhere: {
    title: string;
    /** Under the title, by the live break's minimum box price, formatted. */
    from: (minPrice: string) => string;
    search: { label: string; placeholder: string };
    /** Said in place of the list while nothing matches, and while it loads. */
    empty: string;
    loading: string;
    /** A box's button, and its accessible name by the box's name; the pick's, once chosen. */
    choose: string;
    chooseLabel: (boxName: string) => string;
    chosen: string;
    quantity: string;
    join: string;
    cancel: string;
    loadMore: string;
  };
  inside: {
    quantity: string;
    join: string;
    /** The host's, from a big hit in chat. */
    add: string;
    added: string;
    loading: string;
    close: string;
  };
}

export function defaultLiveBreakRoomCopy(terms: StorefrontTerminology): LiveBreakRoomCopy {
  const { box, item, inventory } = terms;
  const boxes = capitalizeTerm(box.plural);
  const queueCount = (count: number) => `Queue (${countLabel(count)})`;
  return {
    identity: {
      back: "Live breaks",
      home: { back: "Home", close: "Back to home" },
      viewers: (count) => `${countLabel(count)} watching`,
      roster: { title: "Watching", empty: "Nobody else is here yet." },
      profile: "View profile",
    },
    toolbar: {
      label: "Live break tools",
      fairness: "Provably fair",
      share: "Share live break",
      sound: "Mute sound",
      fullscreen: "Full screen",
      more: "More tools",
      close: "Back to live breaks",
      connect: "Connect stream",
    },
    stage: {
      label: "Stage",
      announce: ["The next opening", "is about to begin"],
      hostAnnounce: ["The next opening", "is ready to begin"],
      amount: (amount) => `×${amount}`,
      opening: (current, total) => `Opening ${current} of ${total}`,
      results: "Results",
      total: "Total",
      spent: "Spent",
      each: "each",
      pulls: "Pulls so far",
      carousel: {
        label: "Results",
        previous: "Previous",
        next: "Next",
        slide: (position, count) => `${position} of ${count}`,
      },
      countdown: (seconds) => `Next opening in ${seconds} ${seconds === 1 ? "second" : "seconds"}`,
      countdownNow: "Next opening starting…",
    },
    buy: {
      label: `Buy ${box.plural}`,
      carousel: {
        // Not the bare plural: that is the Packs panel's name, and two regions may not share one.
        label: `${boxes} to choose from`,
        previous: "Previous",
        next: "Next",
        show: (name) => `Show ${name}`,
      },
      quantity: "How many",
      whatsInside: "What's inside",
      join: "Join queue",
      signIn: "Sign in to buy",
      elsewhere: "Want to open something else?",
      empty: `No ${box.plural} to buy here yet.`,
    },
    breaks: {
      title: boxes,
      list: {
        label: `${boxes} in this live break`,
        heading: "Available",
        empty: `No ${box.plural} to buy here yet.`,
      },
      tabs: { label: `${boxes} and queue`, boxes, queue: queueCount },
    },
    queue: {
      title: "Queue",
      summary: (count) => `${countLabel(count)} queued`,
      label: "Opening order",
    },
    chat: {
      sheet: "Chat",
      browse: {
        boxes: `View ${box.plural}`,
        queue: queueCount,
        title: `${boxes} and queue`,
        close: "Close",
      },
      title: "Live break chat",
      transcript: "Live break chat messages",
      jump: "Jump to latest",
      watching: (count) => `${countLabel(count)} watching`,
      empty: "Nobody has said anything yet. Say hello!",
      loading: "Loading chat…",
      host: "Host",
      react: "React",
      reactWith: "React with",
      reply: (name) => `Reply to ${name}`,
      replyingTo: "Replying to",
      cancelReply: "Cancel reply",
      showReplied: "Show the message it answers",
      deleted: "Message deleted",
      emote: "Send an emote",
    },
    results: {
      title: "Results",
      summary: (openings, items, total) =>
        `${countLabel(openings)} ${openings === 1 ? "opening" : "openings"}, ${countLabel(items)} ${items === 1 ? item.singular : item.plural} worth ${total}`,
      items: `Your ${item.plural}`,
      loading: `Loading your ${item.plural}…`,
      retry: "Retry",
      claim: "Claim",
      claimAll: "Claim all",
      sellAll: "Sell all",
      sellSelected: "Sell selected",
      allSold: "All sold",
      sell: "Sell",
      sold: "Sold",
      inInventory: capitalizeTerm(inventory.singular),
      unseen: (count) => `${countLabel(count)} new`,
      close: "Close",
    },
    hostControls: { title: "Host controls", trigger: "Host", close: "Close" },
    elsewhere: {
      title: "Want to open something else?",
      from: (minPrice) => `${boxes} from ${minPrice} in this live break`,
      search: { label: `Search ${box.plural}`, placeholder: `Search ${box.plural}` },
      empty: `No other ${box.plural} match your search.`,
      loading: `Loading ${box.plural}…`,
      choose: "Choose",
      chooseLabel: (boxName) => `Choose ${boxName}`,
      chosen: "Chosen",
      quantity: "How many",
      join: "Join queue",
      cancel: "Cancel",
      loadMore: STOREFRONT_PAGING_COPY.loadMore,
    },
    inside: {
      quantity: "How many",
      join: "Join queue",
      add: "Add to live break",
      added: "In live break",
      loading: "Loading what's inside…",
      close: "Close",
    },
  };
}

/** A live break title's naming policy: the way to it, under the title field, and the dialog it opens. */
export interface LiveBreakNamingPolicyCopy {
  open: string;
  title: string;
  rules: readonly string[];
  close: string;
}

/** The naming policy, the same wherever a host names a live break (creating one, or its settings). */
function namingPolicyCopy(): LiveBreakNamingPolicyCopy {
  return {
    open: "View naming policy",
    title: "Naming policy",
    rules: [
      `Up to ${LIVE_BREAK_TITLE_MAX} characters`,
      "At least one character",
      "No profanity, slurs or links",
    ],
    close: "I understand",
  };
}

// The host's controls --------------------------------------------------------------------------

/**
 * The words a live break's host sees on its page, by control: moving the stage on (Next opening,
 * Continue, Open now), the host controls panel and its dialogs (the settings, Pick, Remove a
 * player, Pause, End), the host's actions on a queue row, their blocked players and the big hits'
 * spotlight. Why a host write was refused, and what one that went through says, are the
 * platform's (`liveBreakHostPlatformWords`). An entry's turn on stage is an Opening; "break" is
 * never said alone.
 */
export interface LiveBreakHostCopy {
  /** Moving the stage on by hand: under the stage, and atop the controls on a phone. */
  sequence: {
    /** The group's name. */
    label: string;
    /** From an empty stage or the results: the next entry goes on stage. */
    next: string;
    /** The last entry's results: the stage goes back to waiting. */
    continue: string;
    /** A Presentation the server should have opened by now. */
    openNow: string;
    /** Next while nothing is queued. */
    queueEmpty: string;
    /** Auto Mode's pause beside it, and its way back. */
    pause: string;
    resume: string;
    /** The panel card's badge while its entry is on stage (the live break's own LIVE is apart). */
    onStage: string;
  };
  /** The host controls panel. */
  controls: {
    /** The panel's name. */
    label: string;
    queueMode: {
      label: string;
      /** The info button's name, and what it says. */
      about: string;
      info: string;
      manual: string;
      auto: string;
    };
    settings: string;
    pick: string;
    removePlayer: string;
    pause: string;
    resume: string;
    /** Under Pause while the host runs the queue by hand. */
    pauseNeedsAuto: string;
    stopPurchases: string;
    allowPurchases: string;
    /** What each does, read with it. */
    stopPurchasesHint: string;
    allowPurchasesHint: string;
    end: string;
    /** A line over the controls while the live break is paused, or new purchases are stopped. */
    status: { paused: string; purchasesStopped: string };
    /** In place of the controls while it Drains. */
    draining: {
      /** How far the queue has to go: "3 entries left · about 4 min"; minutes left out while unknown. */
      progress: (entriesLeft: number, minutes?: number | null) => string;
      body: string;
      staffEnded: string;
    };
  };
  /** The host's actions on a queue row, in its menu. */
  queueRow: { sendToFront: string; remove: string; manage: string };
  /** The live break's settings. */
  settings: {
    title: string;
    /** One sentence under the title: when changes apply. */
    description: string;
    close: string;
    /** The stream field: the same words as Create's. */
    twitch: LiveBreakStreamFieldCopy;
    name: {
      label: string;
      placeholder: string;
      /** Its length against the most it takes: "24/80". */
      count: (length: number) => string;
      /** The naming policy, a dialog opened from beside the count. */
      policy: LiveBreakNamingPolicyCopy;
    };
    buyerQueueCap: LiveBreakEntryCapCopy;
    rate: {
      label: string;
      /** A rate in basis points as a percentage: "1.5%". */
      format: (bps: number) => string;
      /** Under the slider, by the store's maximum formatted. */
      helper: (maxRate: string) => string;
      /** In its place while the saved rate is above a lowered maximum, both formatted. */
      aboveMax: (rate: string, maxRate: string) => string;
    };
    openerDelay: {
      label: string;
      helper: string;
      /** Seconds: "2.5s". */
      format: (seconds: number) => string;
    };
    minBoxPrice: LiveBreakMinPriceCopy;
    restrict: LiveBreakRestrictCopy;
    boxes: {
      title: string;
      /** How many against the most a live break holds: "12 of 50". */
      count: (count: number) => string;
      add: string;
      /** A box picked since the dialog opened. */
      added: string;
      /** What one open of it earns the host, formatted: "+$0.38 per open". */
      perOpen: (amount: string) => string;
      /** A box row's name where it opens the box's contents, by the box's name. */
      viewBox: (name: string) => string;
      overLimit: string;
      /** Why none can be taken out. */
      kept: string;
    };
    cancel: string;
    save: string;
  };
  /** Pick: moving a waiting entry up front. */
  pick: {
    title: string;
    heading: string;
    body: string;
    search: { label: string; placeholder: string };
    /** The list's name. */
    list: string;
    /** Beside the buyer on stage, who can't be picked. */
    current: string;
    loading: string;
    /** Why there is no one to pick, or why the one chosen can't be. */
    empty: string;
    onlyUpNext: string;
    allMovedUp: string;
    gone: string;
    noMatches: string;
    confirm: string;
    cancel: string;
  };
  /** Remove a player: a Block. */
  removePlayer: {
    title: string;
    /** Before anyone is chosen. */
    choose: { heading: string; body: string };
    /** Said with nobody to remove. */
    nobody: string;
    /** Once someone is chosen, by their name. */
    heading: (name: string) => string;
    body: string;
    /** Added while their entry is on stage. */
    onStage: string;
    search: { label: string; placeholder: string };
    list: string;
    /** Said while nobody matches the search, by it. */
    noMatches: (search: string) => string;
    /** What each player is doing here. */
    status: {
      blocked: string;
      onStage: string;
      queued: (count: number) => string;
      watching: string;
      away: string;
    };
    confirm: string;
    cancel: string;
  };
  /** The host's blocked players, with Unblock: in Remove a player and on the host dashboard. */
  blocked: {
    title: string;
    loading: string;
    empty: string;
    /** On a Block staff made. */
    byStaff: string;
    unblock: string;
    unblockLabel: (name: string) => string;
  };
  pause: {
    title: string;
    heading: string;
    body: string;
    confirm: string;
    cancel: string;
    /** Why Pause waits: another write to the live break is still saving. */
    blocked: string;
  };
  end: {
    title: string;
    heading: string;
    body: string;
    /** In place of `body` with nothing queued. */
    bodyEmpty: string;
    /** Its figures: who is queued, how many entries, and when the queue should finish. */
    shoppers: string;
    entries: string;
    finish: string;
    minutes: (minutes: number) => string;
    /** Read in place of a figure not known yet. */
    unavailable: string;
    confirm: string;
    cancel: string;
    /** Why End waits: another write to the live break is still saving. */
    blocked: string;
  };
  /** The big hits held up over the stage, in Manual mode. */
  spotlight: { label: string; dismiss: string; continue: string };
}

/**
 * The stream field's words, the same in Create and in the settings: the host's own Twitch
 * channel, the switch that shows it, the field's button while it does something, and Twitch's
 * word on the channel.
 */
export interface LiveBreakStreamFieldCopy {
  label: string;
  /** Beside the label: the stream is optional. */
  optional: string;
  /** The switch that shows the host's stream beside the live break. */
  link: string;
  /** Before the channel's login, and what the field shows with none. */
  prefix: string;
  placeholder: string;
  /** The field's button, shown only while it does something: link an account, or renew the link. */
  connect: string;
  reconnect: string;
  /** Under the field: the account loading, none linked, a link to renew, the stream off. */
  loading: string;
  noAccount: string;
  renew: string;
  notLinked: string;
  /** The channel as Twitch last saw it: a line under the field and a word beside it. */
  status: LiveBreakStreamStatusCopy;
  retry: string;
  /** A changed channel Twitch hasn't confirmed waits to be saved (the settings). */
  unconfirmed: string;
}

/** Entries per person, in Create and the settings: a stepper, and No limit. */
export interface LiveBreakEntryCapCopy {
  label: string;
  hint: string;
  /** The switch that lifts the limit. */
  noLimit: string;
  /** The stepper's two buttons. */
  fewer: string;
  more: string;
}

/** The minimum box price, in Create and the settings. */
export interface LiveBreakMinPriceCopy {
  label: string;
  hint: string;
  /** What the field shows empty. */
  placeholder: string;
}

/** Only the live break's own boxes sell: a switch in Create's preview and in the settings. */
export interface LiveBreakRestrictCopy {
  label: string;
  hint: string;
}

function streamFieldCopy(): LiveBreakStreamFieldCopy {
  return {
    label: "Twitch stream",
    optional: "Optional",
    link: "Show stream",
    prefix: "twitch.tv/",
    placeholder: "yourchannel",
    connect: "Connect",
    reconnect: "Reconnect",
    loading: "Checking your Twitch account…",
    noAccount: "Connect your Twitch account to show your stream in your live break.",
    renew: "Reconnect your Twitch account to keep showing your stream.",
    notLinked: "Stream not shown",
    status: {
      checking: { detail: "Checking your Twitch channel…", result: "Checking" },
      live: { detail: "Live stream found", result: "Live" },
      offline: { detail: "Your channel is offline right now", result: "Offline" },
      missing: { detail: "Channel not found", result: "Not found" },
      unknown: { detail: "Couldn't check your channel", result: "Unverified" },
    },
    retry: "Retry",
    unconfirmed:
      "Twitch hasn't confirmed this channel yet. Save once it has, or turn off Show stream.",
  };
}

function entryCapCopy(): LiveBreakEntryCapCopy {
  return {
    label: "Entries per person",
    hint: "How many entries each person can have waiting in the queue at once.",
    noLimit: "No limit",
    fewer: "One fewer entry per person",
    more: "One more entry per person",
  };
}

function minPriceCopy(terms: StorefrontTerminology): LiveBreakMinPriceCopy {
  const { box } = terms;
  return {
    label: `Minimum ${box.singular} price`,
    hint: `The lowest ${box.singular} price, before commission, that can be added or bought from outside this live break. Leave it empty for no minimum.`,
    placeholder: "No minimum",
  };
}

function restrictCopy(terms: StorefrontTerminology): LiveBreakRestrictCopy {
  const { box } = terms;
  return {
    label: `Only this live break's ${box.plural}`,
    hint: `Off, viewers and you can also buy any ${box.singular} at or above the minimum price.`,
  };
}

/** A rate in basis points as a percentage, to two places at most: 150 → "1.5%". */
function rateLabel(bps: number): string {
  return `${(bps / 100).toFixed(2).replace(/\.?0+$/, "")}%`;
}

export function defaultLiveBreakHostCopy(terms: StorefrontTerminology): LiveBreakHostCopy {
  const { box } = terms;
  const boxes = capitalizeTerm(box.plural);
  const delaySeconds = LIVE_BREAK_MAX_OPENER_DELAY_MS / 1000;
  return {
    sequence: {
      label: "Opening controls",
      next: "Next opening",
      continue: "Continue",
      openNow: "Open now",
      queueEmpty: "Queue is empty",
      pause: "Pause",
      resume: "Resume",
      onStage: "On stage",
    },
    controls: {
      label: "Host controls",
      queueMode: {
        label: "Queue mode",
        about: "About queue mode",
        info: "Manual: press Next opening to start each opening. Auto mode: openings run on timers. Stopping new purchases leaves the queued entries to open.",
        manual: "Manual",
        auto: "Auto mode",
      },
      settings: "Live break settings",
      pick: "Pick next buyer",
      removePlayer: "Remove a player",
      pause: "Pause live break",
      resume: "Resume live break",
      pauseNeedsAuto: "Pause is available in Auto mode.",
      stopPurchases: "Stop new purchases",
      allowPurchases: "Allow new purchases",
      stopPurchasesHint: "Stops new purchases. Entries already queued still open.",
      allowPurchasesHint: "Lets viewers buy into the queue again.",
      end: "End live break",
      status: {
        paused: "Paused · viewers can still buy",
        purchasesStopped: "New purchases stopped",
      },
      draining: {
        progress: (entriesLeft, minutes) => {
          if (entriesLeft === 0) return "Finishing the last opening";
          const left = `${countLabel(entriesLeft)} ${entriesLeft === 1 ? "entry" : "entries"} left`;
          return minutes === null || minutes === undefined
            ? left
            : `${left} · about ${minutes} min`;
        },
        body: "New purchases are stopped. The queued entries open automatically, then the live break closes.",
        staffEnded: "Staff ended this live break.",
      },
    },
    queueRow: {
      sendToFront: "Send to front",
      remove: "Remove from live break",
      manage: "Manage your entries",
    },
    settings: {
      title: "Live break settings",
      description: `Changes apply when you save. ${boxes} already in the live break stay.`,
      close: "Close settings",
      twitch: streamFieldCopy(),
      name: {
        label: "Title",
        placeholder: "Name your live break",
        count: (length) => `${length}/${LIVE_BREAK_TITLE_MAX}`,
        policy: namingPolicyCopy(),
      },
      buyerQueueCap: entryCapCopy(),
      rate: {
        label: "Commission rate",
        format: rateLabel,
        helper: (maxRate) => `Added on top of every price, up to ${maxRate}.`,
        aboveMax: (rate, maxRate) =>
          `Your ${rate} was set before the maximum dropped to ${maxRate}. Lower it and it can only go up to ${maxRate}.`,
      },
      openerDelay: {
        label: "Opener delay",
        helper: `Delays what viewers see to match your stream, 0 to ${delaySeconds} seconds.`,
        format: (seconds) => `${seconds}s`,
      },
      minBoxPrice: minPriceCopy(terms),
      restrict: restrictCopy(terms),
      boxes: {
        title: boxes,
        count: (count) => `${countLabel(count)} of ${LIVE_BREAK_MAX_BOXES}`,
        add: `Add ${box.plural}`,
        added: "New",
        perOpen: (amount) => `+${amount} per open`,
        viewBox: (name) => `What's inside ${name}`,
        overLimit: `A live break holds up to ${LIVE_BREAK_MAX_BOXES} ${box.plural}. Leave some of the new ones out.`,
        kept: `${boxes} already in the live break can't be taken out.`,
      },
      cancel: "Cancel",
      save: "Save changes",
    },
    pick: {
      title: "Pick next buyer",
      heading: "Choose who goes up front",
      body: "They open right after the entry up next and anyone already moved up. Everyone else keeps their order.",
      search: { label: "Search the queue", placeholder: "Search buyers" },
      list: "Queued entries",
      current: "On stage",
      loading: "Loading the queue…",
      empty: "No one is waiting in the queue.",
      onlyUpNext: "Only the entry up next is waiting.",
      allMovedUp: "Everyone else waiting has already moved up.",
      gone: "That entry isn't waiting anymore. Choose another.",
      noMatches: "No buyers match your search.",
      confirm: "Move up front",
      cancel: "Cancel",
    },
    removePlayer: {
      title: "Remove a player",
      choose: { heading: "Choose who to remove", body: "Pick someone in this live break." },
      nobody: "No one is in this live break yet.",
      heading: (name) => `Remove ${name}?`,
      body: `Their queued ${box.plural} are released, and they're blocked from all your live breaks until you unblock them.`,
      onStage: `The ${box.singular} on stage finishes first.`,
      search: { label: "Search players", placeholder: "Search players" },
      list: "Players",
      noMatches: (search) => `No player matches “${search}”.`,
      status: {
        blocked: "Blocked",
        onStage: "On stage",
        queued: (count) => `${countLabel(count)} queued`,
        watching: "Watching",
        away: "Not here",
      },
      confirm: "Remove from live break",
      cancel: "Cancel",
    },
    blocked: {
      title: "Blocked players",
      loading: "Loading…",
      empty: "You haven't blocked anyone.",
      byStaff: "By staff",
      unblock: "Unblock",
      unblockLabel: (name) => `Unblock ${name}`,
    },
    pause: {
      title: "Pause live break",
      heading: "Pause after the opening on stage",
      body: "Viewers keep their places and can keep buying while it's paused.",
      confirm: "Pause",
      cancel: "Cancel",
      blocked: "Another change is saving…",
    },
    end: {
      title: "End live break",
      heading: "Close the queue to new purchases",
      body: "New purchases stop at once. Queued shoppers keep their places, and their entries open automatically before the live break closes.",
      bodyEmpty: "Nothing is queued, so the live break closes now.",
      shoppers: "Queued shoppers",
      entries: "Entries left",
      finish: "Est. finish",
      minutes: (minutes) => `${minutes} min`,
      unavailable: "Not available",
      confirm: "End live break",
      cancel: "Cancel",
      blocked: "Another change is saving…",
    },
    spotlight: { label: "Big hits", dismiss: "Dismiss", continue: "Continue" },
  };
}

// Creating one ------------------------------------------------------------------------------------

/** A box count as the create wizard says it: "1 box", "12 boxes". */
function boxCount(terms: StorefrontTerminology, count: number): string {
  return `${countLabel(count)} ${count === 1 ? terms.box.singular : terms.box.plural}`;
}

/** A rate in basis points as a percentage, at most two decimals: "1.5%", "2.25%", "0%". */
function ratePercent(bps: number): string {
  return `${Number((bps / 100).toFixed(2))}%`;
}

/** The host's Twitch channel as the stream field reads it: a line under the field, a word beside it. */
export type LiveBreakStreamStatusCopy = Record<
  "checking" | "live" | "offline" | "missing" | "unknown",
  { detail: string; result: string }
>;

/** A choice drawn as a tile: its name and a line under it. */
export interface LiveBreakChoiceCopy {
  label: string;
  hint: string;
}

/** A box search's price range: the two fields' names and what they show empty. */
export interface LiveBreakPriceRangeCopy {
  label: string;
  min: string;
  max: string;
  minPlaceholder: string;
  maxPlaceholder: string;
}

/**
 * The create wizard's words, by step: the frame (each step's title, the rail, Back and Continue),
 * the details (the stream, the title and its naming policy, who can find it, entries per person,
 * the minimum box price), the playlist, the box picker and the preview; then the dialog that says
 * it was created. Why a create was refused, and a list that failed to load, are the platform's
 * (`liveBreakHostPlatformWords`).
 */
export interface LiveBreakCreateCopy {
  wizard: {
    /** The dialog's title on each step. */
    titles: { details: string; playlist: string; boxes: string; preview: string };
    /** The rail's name, each step's short name on it, and a step's place read before its name. */
    steps: {
      label: string;
      details: string;
      playlist: string;
      boxes: string;
      preview: string;
      position: (step: number) => string;
    };
    back: string;
    /** In Back's place on the first step, which closes the wizard. */
    cancel: string;
    /** The header's back button's name. */
    previous: string;
    continue: string;
    create: string;
    close: string;
    /** Why Continue (or Create) waits: said beside it in the footer. */
    blocked: {
      title: string;
      price: string;
      playlist: string;
      boxes: string;
      stream: { checking: string; missing: string; unknown: string };
    };
  };
  details: {
    /** The stream field: the same words as the settings'. */
    stream: LiveBreakStreamFieldCopy;
    title: {
      label: string;
      hint: string;
      placeholder: string;
      /** Its length against the most it takes: "24/80". */
      count: (length: number) => string;
      /** The naming policy, a dialog opened from under the field. */
      policy: LiveBreakNamingPolicyCopy;
    };
    visibility: {
      label: string;
      hint: string;
      public: LiveBreakChoiceCopy;
      private: LiveBreakChoiceCopy;
    };
    buyerQueueCap: LiveBreakEntryCapCopy;
    minBoxPrice: LiveBreakMinPriceCopy;
  };
  playlist: {
    modes: {
      label: string;
      suggested: LiveBreakChoiceCopy;
      random: LiveBreakChoiceCopy;
      custom: LiveBreakChoiceCopy;
    };
    category: { label: string; all: string };
    search: { label: string; placeholder: string };
    /** Random's box limit, its price range and Reroll. */
    random: {
      limit: string;
      fewer: string;
      more: string;
      price: LiveBreakPriceRangeCopy;
      reroll: string;
      rerollLabel: string;
    };
    /** The cards: a Random deal's name by its place, the host's own list's, and their parts. */
    card: {
      luckyMix: (position: number) => string;
      custom: string;
      count: (count: number) => string;
      pick: (name: string) => string;
      edit: (name: string) => string;
      viewBox: (name: string) => string;
    };
    /** Said in place of the cards: Custom before any pick, and why a list came back empty. */
    customEmpty: string;
    loading: string;
    empty: { none: string; noMatches: string; belowMinimum: string; random: string };
    retry: string;
  };
  picker: {
    search: { label: string; placeholder: string };
    category: { label: string; all: string };
    price: LiveBreakPriceRangeCopy;
    onlySelected: string;
    /** How many are picked (and of them, how many the live break already has), and at the most a live break holds. */
    selected: (count: number, inLiveBreak?: number) => string;
    full: (count: number) => string;
    clear: (count: number) => string;
    /** A tile's toggle: Add, and Remove by the box's name; a box already in the live break. */
    add: string;
    remove: (name: string) => string;
    /** A picked tile's toggle, before its price. */
    added: string;
    inLiveBreak: string;
    viewBox: (name: string) => string;
    /** Under a tile's price: what one open earns the host. */
    perOpen: string;
    /** The grid's name. */
    label: string;
    loading: string;
    loadMore: string;
    empty: { noMatches: string; noneSelected: string };
    /** Under no matches: back to every box. */
    clearFilters: string;
    retry: string;
    /** A box's contents, from a tile's What's inside. */
    inside: { loading: string; close: string };
  };
  preview: {
    moderation: { title: string; body: string };
    /** Said in place of the title while it has none. */
    titleRequired: string;
    visibility: string;
    public: string;
    private: string;
    buyerQueueCap: string;
    noLimit: string;
    /** The settings' words for the same switch. */
    restrict: LiveBreakRestrictCopy;
    minBoxPrice: string;
    noMinimum: string;
    playlist: { title: string; label: string; empty: string; remove: (name: string) => string };
    perOpen: string;
  };
  /** The dialog over the new live break: its title and link with Copy, Announce for a public one, then Start. */
  created: {
    title: string;
    body: string;
    /** The link field's name. */
    link: string;
    copy: string;
    copied: string;
    announce: string;
    announced: string;
    start: string;
    close: string;
  };
}

export function defaultLiveBreakCreateCopy(terms: StorefrontTerminology): LiveBreakCreateCopy {
  const { box } = terms;
  const boxes = capitalizeTerm(box.plural);
  const priceRange: LiveBreakPriceRangeCopy = {
    label: "Price",
    min: "Minimum price",
    max: "Maximum price",
    minPlaceholder: "Min",
    maxPlaceholder: "Max",
  };
  return {
    wizard: {
      titles: {
        details: "Create live break",
        playlist: "Choose a playlist",
        boxes: "Customize your playlist",
        preview: "Preview your live break",
      },
      steps: {
        label: "Steps to create a live break",
        details: "Details",
        playlist: "Playlist",
        boxes,
        preview: "Preview",
        position: (step) => `Step ${step}`,
      },
      back: "Back",
      cancel: "Cancel",
      previous: "Previous step",
      continue: "Continue",
      create: "Create live break",
      close: "Close",
      blocked: {
        title: "Give your live break a title",
        price: "Enter the minimum price as an amount, or leave it empty",
        playlist: "Pick a playlist",
        boxes: `Pick at least one ${box.singular}`,
        stream: {
          checking: "Checking your Twitch channel…",
          missing:
            "Your Twitch channel couldn't be found. Turn off Show stream to go on without it.",
          unknown: "Your Twitch channel couldn't be checked. Retry, or turn off Show stream.",
        },
      },
    },
    details: {
      stream: streamFieldCopy(),
      title: {
        label: "Title",
        hint: "Give your live break a name",
        placeholder: "Enter a title",
        count: (length) => `${length}/${LIVE_BREAK_TITLE_MAX}`,
        policy: namingPolicyCopy(),
      },
      visibility: {
        label: "Visibility",
        hint: "Choose who can find your live break",
        public: { label: "Public", hint: "Anyone can find and join" },
        private: { label: "Private", hint: "Invite link only" },
      },
      buyerQueueCap: entryCapCopy(),
      minBoxPrice: minPriceCopy(terms),
    },
    playlist: {
      modes: {
        label: "Start from",
        suggested: { label: "Suggested", hint: "Use one of ours" },
        random: { label: "Random", hint: "Feeling lucky" },
        custom: { label: "Custom", hint: "Build your own" },
      },
      category: { label: "Category", all: "All categories" },
      search: { label: "Search playlists", placeholder: "Search playlists" },
      random: {
        limit: `${boxes} per playlist`,
        fewer: `One fewer ${box.singular} per playlist`,
        more: `One more ${box.singular} per playlist`,
        price: priceRange,
        reroll: "Reroll",
        rerollLabel: "Reroll every playlist",
      },
      card: {
        luckyMix: (position) => `Lucky mix ${position}`,
        custom: "Your playlist",
        count: (count) => boxCount(terms, count),
        pick: (name) => `Pick ${name}`,
        edit: (name) => `Edit ${name}`,
        viewBox: (name) => `What's inside ${name}`,
      },
      customEmpty: `Pick every ${box.singular} yourself on the next step.`,
      loading: "Loading playlists…",
      empty: {
        none: "No playlists yet.",
        noMatches: "No playlists match those filters.",
        belowMinimum: `No playlists have ${box.plural} at your minimum ${box.singular} price.`,
        random: `No ${box.plural} match those filters.`,
      },
      retry: "Retry",
    },
    picker: {
      search: { label: `Search ${box.plural}`, placeholder: `Search ${box.plural}` },
      category: { label: "Category", all: "All categories" },
      price: priceRange,
      onlySelected: "Only selected",
      selected: (count, inLiveBreak = 0) =>
        inLiveBreak > 0
          ? `${countLabel(count)} selected, ${countLabel(inLiveBreak)} already in the live break`
          : `${countLabel(count)} selected`,
      full: (count) => `${countLabel(count)} selected, the most a live break holds`,
      clear: (count) => `Clear selection (${countLabel(count)})`,
      add: "Add",
      remove: (name) => `Remove ${name}`,
      added: "Added",
      inLiveBreak: "In live break",
      viewBox: (name) => `What's inside ${name}`,
      perOpen: "earned per open",
      label: boxes,
      loading: `Loading ${box.plural}…`,
      loadMore: STOREFRONT_PAGING_COPY.loadMore,
      empty: {
        noMatches: `No ${box.plural} match those filters.`,
        noneSelected: `No ${box.plural} selected yet.`,
      },
      clearFilters: "Clear filters",
      retry: "Retry",
      inside: { loading: "Loading what's inside…", close: "Close" },
    },
    preview: {
      moderation: {
        title: "Moderating your live break",
        body: `Remove a player from your host controls or the queue to release their queued ${box.plural} and block them from your live breaks.`,
      },
      titleRequired: "Title required",
      visibility: "Visibility",
      public: "Public",
      private: "Private",
      buyerQueueCap: "Entries per person",
      noLimit: "No limit",
      restrict: restrictCopy(terms),
      minBoxPrice: `Minimum ${box.singular} price`,
      noMinimum: "No minimum",
      playlist: {
        title: "Your playlist",
        label: `${boxes} in your live break`,
        empty: `Pick at least one ${box.singular}.`,
        remove: (name) => `Remove ${name}`,
      },
      perOpen: "earned per open",
    },
    created: {
      title: "You're live!",
      body: "Time to open some big pulls.",
      link: "Live break link",
      copy: "Copy link",
      copied: "Link copied",
      announce: "Announce live break",
      announced: "Announced",
      start: "Start your live break",
      close: "Close",
    },
  };
}

// The host's pages: the dashboard and history -----------------------------------------------------

/** The Host Rate's words: the row, and the form it opens. */
export interface LiveBreakHostRateCopy {
  label: string;
  /** Beside the rate while the host follows the default. */
  default: string;
  edit: string;
  editLabel: string;
  /** A rate in basis points as a percentage: "1.5%". */
  format: (bps: number) => string;
  /** Under the slider: what the rate does, then that it caps at the store's maximum, formatted. */
  help: string;
  max: (maxRate: string) => string;
  /** While the host follows the default rate, formatted. */
  onDefault: (rate: string) => string;
  save: string;
  backToDefault: (rate: string) => string;
  cancel: string;
}

/** The per-box chart's words. */
export interface LiveBreakBoxStatsChartCopy {
  title: string;
  /** The measure's switch and its two options. */
  metric: { label: string; spend: string; opens: string };
  /** The two series, in the legend and before each row's figures. */
  viewers: string;
  host: string;
  /** Under the capped chart: every box, and back. */
  showAll: (count: number) => string;
  showFewer: string;
}

/** A hosted live break as a row: the columns' names, and what a row says in place of a figure. */
export interface LiveBreakHistoryRowCopy {
  columns: { liveBreak: string; commission: string; opens: string; topPull: string };
  /** Read in place of a figure a row doesn't have ("—" is shown). */
  none: string;
  noOpens: string;
  private: string;
  /** On a narrow row, the badge standing in for the tags it leaves out: "+2". */
  moreTags: (count: number) => string;
  /** The badge and link on one still open. */
  live: string;
  goTo: string;
  /** How long it ran: "<1m", "45m", "5h 12m", "2d 3h". */
  duration: (ms: number) => string;
}

/** How long a live break ran, in the buckets history uses: "<1m", "45m", "5h 12m", "2d 3h". */
function durationLabel(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  const days = Math.floor(minutes / 1_440);
  const hours = Math.floor((minutes % 1_440) / 60);
  const rest = minutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${rest}m`;
  return rest > 0 ? `${rest}m` : "<1m";
}

function hostRateCopy(terms: StorefrontTerminology): LiveBreakHostRateCopy {
  return {
    label: "Your commission rate",
    default: "Default",
    edit: "Edit",
    editLabel: "Edit your commission rate",
    format: ratePercent,
    help: `Added to every ${terms.box.singular}'s price in your live breaks, and what you earn per open. It applies from your next live break; live breaks already open keep theirs.`,
    max: (maxRate) => `Up to ${maxRate}.`,
    onDefault: (rate) => `You're on the default rate (${rate}).`,
    save: "Save rate",
    backToDefault: (rate) => `Use the default (${rate})`,
    cancel: "Cancel",
  };
}

function boxStatsChartCopy(terms: StorefrontTerminology): LiveBreakBoxStatsChartCopy {
  const { box } = terms;
  return {
    title: `Per ${box.singular}`,
    metric: { label: "Measure", spend: "Spend", opens: "Opens" },
    viewers: "Viewers",
    host: "You",
    showAll: (count) => `Show all ${boxCount(terms, count)}`,
    showFewer: "Show fewer",
  };
}

function historyRowCopy(): LiveBreakHistoryRowCopy {
  return {
    columns: {
      liveBreak: "Live break",
      commission: "Viewer commission",
      opens: "Opens (viewers / you)",
      topPull: "Top pull",
    },
    none: "None",
    noOpens: "No opens yet.",
    private: "Private",
    moreTags: (count) => `+${countLabel(count)}`,
    live: "LIVE",
    goTo: "Go to live break",
    duration: durationLabel,
  };
}

/**
 * The host dashboard's words, by section: the heading, the commission (the claim, the Host Rate
 * and the all-time figures), every live break's figures with the per-box chart, the most recent
 * live breaks and the blocked players. A failed read or claim is the platform's.
 */
export interface LiveBreakHostDashboardCopy {
  heading: { back: string; title: string; history: string };
  commission: {
    /** Names the panel. */
    label: string;
    loading: string;
    retry: string;
    claim: {
      title: string;
      viewer: string;
      self: string;
      action: string;
      /** Under Claim while there's nothing to claim. */
      nothing: string;
    };
    rate: LiveBreakHostRateCopy;
    allTime: {
      title: string;
      viewer: string;
      self: string;
      claimed: string;
      hosted: string;
    };
  };
  lifetime: {
    title: string;
    loading: string;
    empty: string;
    retry: string;
    totals: { viewerSpend: string; selfSpend: string; viewerOpens: string; selfOpens: string };
    chart: LiveBreakBoxStatsChartCopy;
  };
  recent: {
    title: string;
    viewAll: string;
    loading: string;
    empty: string;
    /** The empty list's way forward. */
    create: string;
    retry: string;
    row: LiveBreakHistoryRowCopy;
  };
  blocked: {
    title: string;
    loading: string;
    empty: string;
    retry: string;
    byStaff: string;
    unblock: string;
    unblockLabel: (name: string) => string;
  };
}

export function defaultLiveBreakHostDashboardCopy(
  terms: StorefrontTerminology,
): LiveBreakHostDashboardCopy {
  const { box } = terms;
  return {
    heading: { back: "Live breaks", title: "Host dashboard", history: "Live break history" },
    commission: {
      label: "Commission",
      loading: "Loading your commission…",
      retry: "Retry",
      claim: {
        title: "Available to claim",
        viewer: "From viewers",
        self: "From your own opens",
        action: "Claim",
        nothing: "Nothing to claim yet.",
      },
      rate: hostRateCopy(terms),
      allTime: {
        title: "All time",
        viewer: "Earned from viewers",
        self: "Earned from your opens",
        claimed: "Claimed",
        hosted: "Live breaks hosted",
      },
    },
    lifetime: {
      title: "All your live breaks",
      loading: `Loading your ${box.plural}…`,
      empty: `No ${box.plural} opened yet.`,
      retry: "Retry",
      totals: {
        viewerSpend: "Viewer spend",
        selfSpend: "Your spend",
        viewerOpens: "Viewer opens",
        selfOpens: "Your opens",
      },
      chart: boxStatsChartCopy(terms),
    },
    recent: {
      title: "Recent live breaks",
      viewAll: "View all",
      loading: "Loading live breaks…",
      empty: "You haven't hosted a live break yet.",
      create: "Create live break",
      retry: "Retry",
      row: historyRowCopy(),
    },
    blocked: {
      title: "Blocked players",
      loading: "Loading blocked players…",
      empty: "You haven't blocked anyone.",
      retry: "Retry",
      byStaff: "By staff",
      unblock: "Unblock",
      unblockLabel: (name) => `Unblock ${name}`,
    },
  };
}

/**
 * The host's history words: the list of every live break they hosted, and one of them in full
 * (its status and times, its figures split between viewers and the host, its #1 boxes, the
 * per-box chart and its top pulls). A failed read is the platform's.
 */
export interface LiveBreakHistoryCopy {
  heading: { back: string; title: string };
  list: {
    label: string;
    loading: string;
    loadingMore: string;
    empty: string;
    /** The empty list's way forward. */
    create: string;
    loadMore: string;
    showing: (shown: number) => string;
    retry: string;
    row: LiveBreakHistoryRowCopy;
  };
  detail: {
    /** The page's back link and the title it shows before the live break loads. */
    back: string;
    title: string;
    loading: string;
    retry: string;
    /** Someone else's live break, or one that doesn't exist. */
    missing: string;
    summary: {
      label: string;
      live: string;
      closed: string;
      goTo: string;
      opened: string;
      ranFor: string;
      rate: string;
      private: string;
    };
    figures: {
      commission: string;
      spend: string;
      opens: string;
      viewers: string;
      you: string;
      empty: string;
      topBySpend: string;
      topByOpens: string;
      opensCount: (count: number) => string;
    };
    duration: (ms: number) => string;
    rate: (bps: number) => string;
    chart: LiveBreakBoxStatsChartCopy;
    pulls: { title: string; label: string; by: (name: string) => string };
  };
}

export function defaultLiveBreakHistoryCopy(terms: StorefrontTerminology): LiveBreakHistoryCopy {
  return {
    heading: { back: "Host dashboard", title: "Live break history" },
    list: {
      label: "Live breaks you hosted",
      loading: "Loading live breaks…",
      loadingMore: "Loading more live breaks…",
      empty: "You haven't hosted a live break yet.",
      create: "Create live break",
      loadMore: STOREFRONT_PAGING_COPY.loadMore,
      showing: STOREFRONT_PAGING_COPY.showing,
      retry: "Retry",
      row: historyRowCopy(),
    },
    detail: {
      back: "Live break history",
      title: "Live break",
      loading: "Loading live break…",
      retry: "Retry",
      missing: "This live break isn't in your history.",
      summary: {
        label: "Live break details",
        live: "LIVE",
        closed: "CLOSED",
        goTo: "Go to live break",
        opened: "Opened",
        ranFor: "Ran for",
        rate: "Commission rate",
        private: "Private",
      },
      figures: {
        commission: "Commission",
        spend: "Spend",
        opens: "Opens",
        viewers: "Viewers",
        you: "You",
        empty: "No opens yet.",
        topBySpend: "#1 by spend",
        topByOpens: "#1 by opens",
        opensCount: (count) => `${countLabel(count)} ${count === 1 ? "open" : "opens"}`,
      },
      duration: durationLabel,
      rate: ratePercent,
      chart: boxStatsChartCopy(terms),
      pulls: {
        title: "Top pulls",
        label: "Top pulls, most valuable first",
        by: (name) => `Pulled by ${name}`,
      },
    },
  };
}

// The live-breaks widget ----------------------------------------------------------------------------

/**
 * The live-breaks widget's words: the dock the store's frame mounts once, its rows (a sponsored or
 * pinned live break with its stage's phase, Join and the View menu; a promoted stream, live on
 * Twitch with no live break; the shopper's queue and hosting Return rows), the stream picker, and
 * the picture-in-picture frame around the Twitch player. A failed Twitch link is the storefront's.
 */
export interface LiveBreakWidgetCopy {
  /** The dock's name, as a landmark. */
  label: string;
  /** A live break's line under its host, by its stage's phase. */
  phase: Record<LiveBreakRevealPhase, string>;
  /** Read in place of the phase while a pinned live break loads. */
  loading: string;
  /** A promoted stream's line: it has no live break to show. */
  liveOnTwitch: string;
  /** Read beside the live dot, which says the stream is on air. */
  live: string;
  /** Into the live break, and its accessible name by the host. */
  join: string;
  joinLabel: (name: string) => string;
  /** The View menu, its accessible name by the host, and its three choices. */
  view: string;
  viewLabel: (name: string) => string;
  pictureInPicture: string;
  watchOnTwitch: string;
  /** Read after Watch on Twitch, which leaves the store. */
  newTab: string;
  hide: string;
  /** Folds the row to its pill, by the host. */
  collapse: (name: string) => string;
  /** The folded row, which unfolds it: "Live: mira". */
  pill: (name: string) => string;
  /** The picker on the row's left side, while there is more than one stream to choose. */
  picker: {
    /** Read after the row's name and line: the row opens the picker. */
    hint: string;
    /** The picker's name. */
    label: string;
    /** Its groups: sponsored live breaks and promoted streams, then live breaks watched lately. */
    sponsored: string;
    recent: string;
    /** A recent live break's line. */
    liveBreak: string;
  };
  /** The host's Return row, with the count queued in their live break. */
  hosting: {
    eyebrow: string;
    title: string;
    /** The count as the pill shows it. */
    count: (count: number) => string;
    /** Read after the count. */
    countLabel: string;
  };
  /** The shopper's Return row: their soonest place in a live break's queue. */
  queue: {
    eyebrow: string;
    /** The eyebrow, and the place's badge, while that entry opens next: as the queue says it. */
    upNext: string;
    next: string;
    title: string;
    /** The place as the row shows it: "#3". */
    place: (position: number) => string;
    /** Read after the row: whose live break, by the host's name. */
    host: (hostName: string) => string;
  };
  /** The picture-in-picture frame around the Twitch player. */
  pip: {
    /** The frame's name, by the stream's name. */
    label: (name: string) => string;
    /** Under the name while the stream plays. */
    viewing: string;
    close: string;
    /** Signed out of the store. */
    logIn: string;
    /** Twitch not linked: links it in a popup, whose Twitch sign-in signs the player in too. */
    signInToTwitch: string;
    /** Where the player can't keep a Twitch sign-in (Safari, iOS): link anyway, and why it won't help here. */
    connectTwitch: string;
    otherBrowser: string;
    /** Linked: the status, read with its explainer's name, and the explainer. */
    connected: string;
    connectedHint: string;
    connectedTitle: string;
    connectedBody: string;
    /** Signs the player in again, for a shopper seeing ads they shouldn't. */
    adsQuestion: string;
    refresh: string;
    /** In place of refresh where the player can't keep a sign-in. */
    reconnectOtherBrowser: string;
  };
}

export function defaultLiveBreakWidgetCopy(terms: StorefrontTerminology): LiveBreakWidgetCopy {
  const { box } = terms;
  return {
    label: "Live breaks",
    phase: {
      idle: `Waiting for the next ${box.singular}`,
      presenting: `Presenting the next ${box.singular}`,
      revealed: `Opening ${box.plural} now`,
    },
    loading: "Loading…",
    liveOnTwitch: "Live on Twitch",
    live: "Live",
    join: "Join",
    joinLabel: (name) => `Join ${name}'s live break`,
    view: "View",
    viewLabel: (name) => `View ${name}'s stream`,
    pictureInPicture: "Picture-in-picture",
    watchOnTwitch: "Watch on Twitch",
    newTab: "(opens in a new tab)",
    hide: "Hide widget",
    collapse: (name) => `Collapse ${name}'s stream`,
    pill: (name) => `Live: ${name}`,
    picker: {
      hint: "Choose a stream",
      label: "Streams",
      sponsored: "Sponsored",
      recent: "Recent",
      liveBreak: "Live break",
    },
    hosting: {
      eyebrow: "Hosting",
      title: "Return to your live break",
      count: countLabel,
      countLabel: "in the queue",
    },
    queue: {
      eyebrow: "In queue",
      upNext: "Up next",
      next: "Next",
      title: "Return to the live break",
      place: placeLabel,
      host: (hostName) => `${hostName}'s live break`,
    },
    pip: {
      label: (name) => `${name} in picture-in-picture`,
      viewing: "Viewing",
      close: "Close picture-in-picture",
      logIn: "Log in",
      signInToTwitch: "Sign in to Twitch",
      connectTwitch: "Connect Twitch",
      otherBrowser: "Connect in a different browser to watch ad-free.",
      connected: "Connected",
      connectedHint: "About your Twitch connection",
      connectedTitle: "Twitch connected",
      connectedBody: "Your Twitch account is connected.",
      adsQuestion: "Seeing ads you shouldn't?",
      refresh: "Refresh Twitch connection",
      reconnectOtherBrowser: "Reconnect in a different browser to watch ad-free.",
    },
  };
}

// The account's Twitch connection -------------------------------------------------------------------

/**
 * The account's Twitch connection: whether the shopper's Twitch account is linked (their login, and
 * whether the link still works), with Connect, Reconnect and Disconnect. A failed link or unlink is
 * the storefront's.
 */
export interface TwitchConnectionCopy {
  title: string;
  description: string;
  /** Where the link stands: the badge, and the line beside it (by the account's name once linked). */
  status: {
    loading: string;
    notLinked: { badge: string; text: string };
    linked: { badge: string; text: (name: string) => string };
    reconnect: { badge: string; text: (name: string) => string };
    /** The store can't link Twitch right now. */
    unavailable: { badge: string; text: string };
  };
  connect: string;
  reconnect: string;
  disconnect: string;
}

export function defaultTwitchConnectionCopy(): TwitchConnectionCopy {
  return {
    title: "Twitch",
    description:
      "Link your Twitch account to get subscriber priority in live breaks and to watch streams signed in.",
    status: {
      loading: "Checking your Twitch connection…",
      notLinked: { badge: "Not connected", text: "No Twitch account is linked." },
      linked: { badge: "Connected", text: (name) => `Linked to ${name} on Twitch.` },
      reconnect: {
        badge: "Reconnect required",
        text: (name) => `Twitch stopped accepting the link to ${name}. Reconnect to keep it.`,
      },
      unavailable: {
        badge: "Not available",
        text: "Linking Twitch isn't available right now.",
      },
    },
    connect: "Connect Twitch",
    reconnect: "Reconnect Twitch",
    disconnect: "Disconnect Twitch",
  };
}
